import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { Clock, ArrowLeft, Mail, Linkedin } from 'lucide-react';
import SEO from '../components/SEO';
import { GUIDE_BY_SLUG, AUTHOR, AUTHOR_URL, GUIDES, HELP_ADVICE_INDEX_URL } from '../data/guides';

/**
 * Individual guide reader. Renders the section body with typography rules
 * matching the site's editorial look, and emits three JSON-LD blocks
 * (Article, BreadcrumbList, FAQPage) so Google and answer-engines can index
 * the piece properly.
 */
const HelpAdviceGuidePage = () => {
  const { slug } = useParams();
  const guide = GUIDE_BY_SLUG[slug];
  if (!guide) return <Navigate to={HELP_ADVICE_INDEX_URL} replace />;

  const siteOrigin = typeof window !== 'undefined' ? window.location.origin : 'https://drtoolsuk.com';
  const canonical = `${siteOrigin}/help-advice/${guide.slug}`;

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: guide.title,
    description: guide.excerpt,
    image: [guide.hero_image],
    datePublished: guide.date,
    dateModified: guide.updated || guide.date,
    author: {
      '@type': 'Person',
      '@id': `${siteOrigin}${AUTHOR_URL}#person`,
      name: AUTHOR.name,
      url: `${siteOrigin}${AUTHOR_URL}`,
      image: `${siteOrigin}${AUTHOR.image}`,
      jobTitle: AUTHOR.role,
      description: AUTHOR.short_bio,
      sameAs: Object.values(AUTHOR.links).filter(Boolean),
    },
    publisher: {
      '@type': 'Organization',
      name: 'Dr Tools UK',
      url: 'https://drtoolsuk.com',
      logo: { '@type': 'ImageObject', url: `${siteOrigin}/logo192.png` },
    },
    mainEntityOfPage: { '@type': 'WebPage', '@id': canonical },
    keywords: guide.keywords.join(', '),
  };

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${siteOrigin}/` },
      { '@type': 'ListItem', position: 2, name: 'Help & Advice', item: `${siteOrigin}${HELP_ADVICE_INDEX_URL}` },
      { '@type': 'ListItem', position: 3, name: guide.title, item: canonical },
    ],
  };

  const faqJsonLd = guide.faqs && guide.faqs.length ? {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: guide.faqs.map(f => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  } : null;

  return (
    <article className="max-w-3xl mx-auto px-4 py-8" data-testid={`help-advice-guide-${guide.slug}`}>
      <SEO title={guide.title} description={guide.excerpt} canonical={canonical} image={guide.hero_image} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbJsonLd) }} />
      {faqJsonLd && <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }} />}

      <nav className="text-xs text-neutral-500 mb-4" aria-label="Breadcrumb">
        <Link to="/" className="hover:text-[#F46A25]">Home</Link>
        <span className="mx-1">/</span>
        <Link to={HELP_ADVICE_INDEX_URL} className="hover:text-[#F46A25]">Help &amp; Advice</Link>
        <span className="mx-1">/</span>
        <span className="text-[#12263A] font-semibold line-clamp-1">{guide.title}</span>
      </nav>

      <Link to={HELP_ADVICE_INDEX_URL} className="inline-flex items-center gap-1 text-sm text-[#F46A25] font-bold hover:underline mb-4">
        <ArrowLeft className="w-4 h-4" /> All guides
      </Link>

      <div className="text-[10px] text-[#F46A25] font-bold uppercase tracking-wider mb-2">{guide.category}</div>
      <h1 className="text-3xl md:text-5xl font-black text-[#12263A] leading-tight">{guide.title}</h1>
      <p className="text-neutral-600 text-lg mt-3">{guide.excerpt}</p>

      <div className="flex items-center gap-3 mt-4 mb-6 text-sm text-neutral-600 flex-wrap">
        <Link to={AUTHOR_URL} className="flex items-center gap-2 hover:text-[#F46A25]" data-testid="guide-author-link">
          <img src={AUTHOR.image} alt={AUTHOR.name} width="32" height="32" className="w-8 h-8 rounded-full bg-white border border-neutral-200 object-contain p-1" />
          <span className="font-semibold text-[#12263A]">{AUTHOR.name}</span>
        </Link>
        <span className="text-neutral-400">·</span>
        <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {guide.reading_time} min read</span>
        <span className="text-neutral-400">·</span>
        <time dateTime={guide.date}>Updated {new Date(guide.updated || guide.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</time>
      </div>

      <img src={guide.hero_image} alt={guide.title} width="1200" height="675" className="w-full aspect-[16/9] object-cover rounded-sm border border-neutral-200 mb-6" />

      <div className="prose prose-neutral max-w-none">
        {guide.sections.map((s, i) => <GuideSection key={i} section={s} />)}
      </div>

      {guide.faqs && guide.faqs.length > 0 && (
        <section className="mt-10 border-t border-neutral-200 pt-6" data-testid="guide-faqs">
          <h2 className="text-2xl font-black text-[#12263A] mb-4">Frequently asked</h2>
          <div className="space-y-4">
            {guide.faqs.map((f, i) => (
              <details key={i} className="bg-neutral-50 border border-neutral-200 rounded-sm p-4 open:border-[#F46A25]">
                <summary className="font-bold text-[#12263A] cursor-pointer">{f.q}</summary>
                <p className="text-neutral-700 mt-2">{f.a}</p>
              </details>
            ))}
          </div>
        </section>
      )}

      <AuthorBox />

      <section className="mt-10 border-t border-neutral-200 pt-6">
        <h2 className="text-xl font-black text-[#12263A] mb-4">More guides</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {GUIDES.filter(g => g.slug !== guide.slug).slice(0, 3).map(g => (
            <Link key={g.slug} to={`/help-advice/${g.slug}`} className="flex gap-3 border border-neutral-200 hover:border-[#F46A25] rounded-sm p-3 transition-colors">
              <img src={g.hero_image} alt={g.title} width="120" height="90" className="w-28 h-20 object-cover rounded-sm flex-shrink-0" />
              <div className="min-w-0">
                <div className="text-[10px] text-[#F46A25] font-bold uppercase">{g.category}</div>
                <div className="font-bold text-[#12263A] text-sm leading-tight line-clamp-2">{g.title}</div>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </article>
  );
};

const GuideSection = ({ section }) => {
  switch (section.type) {
    case 'h2':
      return <h2 className="text-2xl font-black text-[#12263A] mt-8 mb-3">{section.text}</h2>;
    case 'p':
      return <p className="text-neutral-700 leading-relaxed mb-4">{section.text}</p>;
    case 'ul':
      return (
        <ul className="list-disc pl-6 mb-4 space-y-1.5 text-neutral-700">
          {section.items.map((it, i) => <li key={i}>{it}</li>)}
        </ul>
      );
    case 'ol':
      return (
        <ol className="list-decimal pl-6 mb-4 space-y-1.5 text-neutral-700">
          {section.items.map((it, i) => <li key={i}>{it}</li>)}
        </ol>
      );
    case 'callout':
      return (
        <div className={`my-6 border-l-4 rounded-sm p-4 ${section.tone === 'orange' ? 'border-[#F46A25] bg-orange-50' : 'border-blue-500 bg-blue-50'}`}>
          {section.title && <div className="font-black text-[#12263A] mb-1">{section.title}</div>}
          <div className="text-sm text-neutral-700">{section.text}</div>
        </div>
      );
    case 'table':
      return (
        <div className="my-6 overflow-x-auto">
          <table className="w-full border-collapse border border-neutral-200 text-sm">
            <thead>
              <tr className="bg-[#12263A] text-white">
                {section.headers.map((h, i) => <th key={i} className="text-left px-3 py-2 font-bold">{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {section.rows.map((row, i) => (
                <tr key={i} className={i % 2 ? 'bg-neutral-50' : 'bg-white'}>
                  {row.map((cell, j) => <td key={j} className="px-3 py-2 border-t border-neutral-200">{cell}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    default:
      return null;
  }
};

const AuthorBox = () => (
  <section className="mt-10 border border-neutral-200 rounded-sm p-5 bg-neutral-50" data-testid="guide-author-box">
    <div className="text-[10px] text-[#F46A25] font-bold uppercase tracking-wider mb-2">Written by</div>
    <div className="flex flex-col md:flex-row gap-4 items-start">
      <img src={AUTHOR.image} alt={AUTHOR.name} width="80" height="80" className="w-20 h-20 rounded-full bg-white border border-neutral-200 object-contain p-2 flex-shrink-0" />
      <div className="flex-1">
        <h3 className="text-xl font-black text-[#12263A]">{AUTHOR.name}</h3>
        <div className="text-sm text-neutral-500 font-semibold">{AUTHOR.role}</div>
        <p className="text-sm text-neutral-700 mt-2">{AUTHOR.bio}</p>
        <div className="flex items-center gap-3 mt-3 text-sm">
          <Link to={AUTHOR_URL} className="text-[#F46A25] font-bold hover:underline">Full profile →</Link>
          <a href={`mailto:${AUTHOR.email}`} className="flex items-center gap-1 text-neutral-600 hover:text-[#F46A25]"><Mail className="w-3.5 h-3.5" /> Email</a>
          {AUTHOR.links.linkedin && (
            <a href={AUTHOR.links.linkedin} rel="noopener noreferrer" target="_blank" className="flex items-center gap-1 text-neutral-600 hover:text-[#F46A25]"><Linkedin className="w-3.5 h-3.5" /> LinkedIn</a>
          )}
        </div>
      </div>
    </div>
  </section>
);

export default HelpAdviceGuidePage;
